import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Application } from '@/types';
import { getApplicationStatusLabel } from '@/types/application';
import { AppWindow, Globe } from 'lucide-react';
import { useApplications } from '@/store/application';
import { Button } from '@/components/ui/Button';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { ComposedTable, AvatarCell, StatusCell, ActionCell, type FilterDef, type FilterValues } from '@/components/table';
import { PageLayout } from '@/components/page-layout';

const FILTERS: FilterDef[] = [
    { type: 'radio', key: 'config', label: 'Configuration', options: ['All', 'Configured', 'Not Configured'] },
];

export const ApplicationsPage = () => {
    const [apps, setApps] = useState<Application[]>([]);
    const [loading, setLoading] = useState(true);
    const listApplications = useApplications((state) => state.list);
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    useEffect(() => {
        setBreadcrumbs([{ name: 'Applications' }]);
        listApplications().then(setApps).catch(console.error).finally(() => setLoading(false));
    }, [listApplications, setBreadcrumbs]);

    const columns = [
        {
            id: 'name',
            header: 'Application Name',
            accessorKey: 'name',
            cell: ({ row }: any) => {
                const a = row.original as Application;
                const idx = apps.indexOf(a);
                return (
                    <AvatarCell
                        name={a.name}
                        subtitle={a.description || 'App Integration'}
                        index={idx}
                        onClick={() => navigate(`/m/applications/${a.id}`)}
                    />
                );
            },
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }: any) => {
                const a = row.original as Application;
                const label = getApplicationStatusLabel(a.status);
                return <StatusCell status={label === 'active' ? 'Active' : label === 'beta' ? 'Active' : 'Inactive'} />;
            },
        },
        {
            id: 'base_url',
            header: 'Endpoints',
            cell: ({ row }: any) => {
                const a = row.original as Application;
                return a.base_url ? (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                        <Globe className="w-3.5 h-3.5" />
                        <span className="font-mono">{a.base_url}</span>
                    </div>
                ) : (
                    <span className="text-xs text-slate-400">Not configured</span>
                );
            },
        },
        {
            id: 'created_at',
            header: 'Created Date',
            cell: ({ row }: any) => {
                const a = row.original as Application;
                if (!a.created_at) return <span className="text-[13px] text-slate-400">—</span>;
                const date = new Date(a.created_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                });
                return <span className="text-[13px] text-slate-500 dark:text-slate-400">{date}</span>;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            meta: { align: 'right' as const },
            cell: ({ row }: any) => <ActionCell onClick={() => navigate(`/m/applications/${row.original.id}`)} />,
        },
    ];

    const filterFn = (row: Application, values: FilterValues) => {
        const config = values['config'] as string;
        if (config && config !== 'All') {
            if (config === 'Configured' && !row.base_url) return false;
            if (config === 'Not Configured' && !!row.base_url) return false;
        }
        return true;
    };

    const statusField = (a: Application) => {
        const label = getApplicationStatusLabel(a.status);
        return label === 'active' || label === 'beta' ? 'Active' : 'Inactive';
    };

    const emptyState = (
        <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <AppWindow className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No applications found</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">Start by creating your first application integration.</p>
            <Button className="mt-6 bg-[#1e293b] hover:bg-slate-800 text-white rounded-full px-6" onClick={() => navigate('/m/applications/create')}>
                Create first application
            </Button>
        </div>
    );

    return (
        <PageLayout title="Applications" description="Manage client applications and endpoints.">
            {loading ? (
                <div className="flex justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
                    <Loader />
                </div>
            ) : (
                <ComposedTable
                    data={apps}
                    columns={columns}
                    searchPlaceholder="Search applications by name or description..."
                    statusOptions={['All', 'Active', 'Inactive']}
                    statusField={statusField}
                    filters={FILTERS}
                    filterFn={filterFn}
                    onAdd={() => navigate('/m/applications/create')}
                    emptyState={emptyState}
                    itemLabel="applications"
                />
            )}
        </PageLayout>
    );
};
